import {
  DownloadOutlined,
  EditOutlined,
  LockOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Empty, Input, Tooltip } from 'antd';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';

import { commandKey, commandLine } from 'helpers/cliCommands';
import { CliCommandSpec, CliRecipe } from 'types/cli';

type Tab = 'tasks' | 'commands';

interface CliCommandPaletteProps {
  commands: CliCommandSpec[];
  recipes: CliRecipe[];
  onInsert: (line: string) => void;
  mode?: Tab;
}

const REVEAL = { duration: 0.16, ease: [0.22, 1, 0.36, 1] as const };

const matches = (needle: string, ...haystack: (string | null | undefined)[]) =>
  !needle ||
  haystack.some(value => (value ?? '').toLowerCase().includes(needle));

function CliCommandPalette({
  commands,
  recipes,
  onInsert,
  mode,
}: Readonly<CliCommandPaletteProps>) {
  const [tab, setTab] = useState<Tab>(mode ?? 'tasks');
  const [search, setSearch] = useState('');
  const [openRecipe, setOpenRecipe] = useState<string | null>(null);

  const needle = search.trim().toLowerCase();

  const visibleRecipes = useMemo(
    () =>
      recipes.filter(
        recipe =>
          matches(needle, recipe.title, recipe.task) ||
          recipe.steps.some(step => matches(needle, step.line, step.why)),
      ),
    [recipes, needle],
  );

  const visibleCommands = useMemo(
    () =>
      commands.filter(command =>
        matches(
          needle,
          command.category,
          command.command,
          command.summary,
          command.usage,
        ),
      ),
    [commands, needle],
  );

  const activeTab: Tab =
    mode ??
    (needle && !visibleRecipes.length && visibleCommands.length
      ? 'commands'
      : tab);

  const renderTasks = () => {
    if (!visibleRecipes.length)
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No matching tasks"
        />
      );

    return visibleRecipes.map(recipe => {
      const isOpen = openRecipe === recipe.id;
      return (
        <div className="CliCommandPalette__recipe" key={recipe.id}>
          <button
            type="button"
            className="CliCommandPalette__recipe-head"
            aria-expanded={isOpen}
            onClick={() => setOpenRecipe(isOpen ? null : recipe.id)}
          >
            <span className="CliCommandPalette__recipe-title">
              {recipe.title}
            </span>
            <span className="CliCommandPalette__recipe-task">
              {recipe.task}
            </span>
          </button>

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.ol
                key="steps"
                className="CliCommandPalette__steps"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4, transition: { duration: 0.1 } }}
                transition={REVEAL}
              >
                {recipe.steps.map(step => (
                  <li className="CliCommandPalette__step" key={step.line}>
                    <button
                      type="button"
                      className="CliCommandPalette__step-line"
                      disabled={!step.allowed}
                      title={
                        step.unsupported
                          ? `Not available in the browser terminal: ${step.unsupported}`
                          : step.allowed
                            ? 'Put this in the prompt'
                            : 'Your permission does not cover this command'
                      }
                      onClick={() => onInsert(step.line)}
                    >
                      <code>{step.line}</code>
                      {!step.allowed && <LockOutlined />}
                    </button>
                    <p className="CliCommandPalette__step-why">{step.why}</p>
                  </li>
                ))}
              </motion.ol>
            )}
          </AnimatePresence>
        </div>
      );
    });
  };

  const renderCommands = () => {
    if (!visibleCommands.length)
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No matching commands"
        />
      );

    return visibleCommands.map(command => {
      const line = commandLine(command);
      const blocked =
        command.unsupported ??
        (command.allowed ? null : `needs ${command.permission} permission`);

      return (
        <Tooltip
          key={commandKey(command)}
          title={blocked ?? command.summary}
          placement="right"
        >
          <button
            type="button"
            className="CliCommandPalette__command"
            disabled={!!blocked}
            onClick={() => onInsert(line)}
          >
            <code className="CliCommandPalette__command-line">
              {line}
              {command.transfer === 'download' && (
                <DownloadOutlined className="CliCommandPalette__transfer" />
              )}
              {command.transfer === 'upload' && (
                <UploadOutlined className="CliCommandPalette__transfer" />
              )}
              {command.transfer === 'edit' && (
                <EditOutlined className="CliCommandPalette__transfer" />
              )}
            </code>
            <span className="CliCommandPalette__command-summary">
              {command.summary}
            </span>
            {blocked && <LockOutlined />}
          </button>
        </Tooltip>
      );
    });
  };

  return (
    <div className="CliCommandPalette">
      <Input
        allowClear
        autoComplete="off"
        size="small"
        prefix={<SearchOutlined />}
        placeholder={
          mode === 'tasks'
            ? 'Search tasks'
            : mode === 'commands'
              ? 'Search commands'
              : 'Search tasks and commands'
        }
        value={search}
        onChange={event => setSearch(event.target.value)}
      />

      {!mode && (
        <div className="CliCommandPalette__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'tasks'}
            className={activeTab === 'tasks' ? 'is-active' : ''}
            onClick={() => setTab('tasks')}
          >
            Shortcuts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'commands'}
            className={activeTab === 'commands' ? 'is-active' : ''}
            onClick={() => setTab('commands')}
          >
            Commands
          </button>
        </div>
      )}

      <div className="CliCommandPalette__list">
        {activeTab === 'tasks' ? renderTasks() : renderCommands()}
      </div>
    </div>
  );
}

export default CliCommandPalette;
